import DOMPurify from 'dompurify';
import { generateTopicUrl } from './urlUtils'

/**
 * Parses dictionary-specific formatting:
 * - (bkz: baslik) -> <a href="/baslik--baslik" class="bkz-link">bkz: baslik</a>
 * - *baslik* -> <a href="/baslik--baslik" class="info-puff" title="baslik">*</a>
 */
export function parseDictionaryContent(content: string): string {
    // 1. Handle (bkz: topic)
    const bkzRegex = /\(bkz:\s*([^)]+)\)/g
    let parsedContent = content.replace(bkzRegex, (_, topic) => {
        const trimmedTopic = DOMPurify.sanitize(topic.trim(), { ALLOWED_TAGS: [] })
        const url = generateTopicUrl(trimmedTopic, trimmedTopic)
        return `<a href="${DOMPurify.sanitize(url)}" class="bkz-link">bkz: ${trimmedTopic}</a>`
    })

    // 2. Handle (hede: topic) -> topic link
    const hedeRegex = /\(hede:\s*([^)]+)\)/g
    parsedContent = parsedContent.replace(hedeRegex, (_, topic) => {
        const trimmedTopic = DOMPurify.sanitize(topic.trim(), { ALLOWED_TAGS: [] })
        const url = generateTopicUrl(trimmedTopic, trimmedTopic)
        return `<a href="${DOMPurify.sanitize(url)}">${trimmedTopic}</a>`
    })

    // 3. Handle *topic* -> info puff
    const puffRegex = /\*([^*]+)\*/g
    parsedContent = parsedContent.replace(puffRegex, (_, topic) => {
        const trimmedTopic = DOMPurify.sanitize(topic.trim(), { ALLOWED_TAGS: [] })
        const url = generateTopicUrl(trimmedTopic, trimmedTopic)
        return `<a href="${DOMPurify.sanitize(url)}" class="info-puff" title="${trimmedTopic}">*</a>`
    })

    // 4. Handle (link: url) -> external link
    const linkRegex = /\(link:\s*([^)]+)\)/g
    parsedContent = parsedContent.replace(linkRegex, (_, url) => {
        const trimmedUrl = DOMPurify.sanitize(url.trim())
        const displayUrl = trimmedUrl.replace(/^https?:\/\//, '').split('/')[0]
        return `<a href="${trimmedUrl}" target="_blank" rel="noopener noreferrer" class="external-link" title="${trimmedUrl}">${displayUrl}</a>`
    })

    // 5. Handle --- spoiler --- blocks
    const spoilerRegex = /--- spoiler ---\s*([\s\S]*?)--- spoiler ---/g
    parsedContent = parsedContent.replace(spoilerRegex, (_, content) => {
        const sanitizedContent = DOMPurify.sanitize(content)
        return `<span class="spoiler-block" data-spoiler="true">${sanitizedContent}</span>`
    })

    // 6. Handle #topic-title (Mavi Link)
    const topicTagRegex = /#([\w\-ğüşıöçĞÜŞİÖÇ]+)/g
    parsedContent = parsedContent.replace(topicTagRegex, (_, topic) => {
        const url = generateTopicUrl(topic, topic)
        return `<a href="${url}" class="mavi-link">#${topic}</a>`
    })

    // 7. Handle @user (Profile Link)
    const userTagRegex = /@([\w\-ğüşıöçĞÜŞİÖÇ]+)/g
    parsedContent = parsedContent.replace(userTagRegex, (_, username) => {
        return `<a href="/profile/${username}" class="user-link">@${username}</a>`
    })

    // Final sanitization of the entire content
    return DOMPurify.sanitize(parsedContent, {
        ALLOWED_TAGS: ['a', 'span'],
        ALLOWED_ATTR: ['href', 'class', 'title', 'target', 'rel', 'data-spoiler']
    })
}

/**
 * Extracts all image URLs from a content string.
 * Supports jpg, jpeg, png, webp, gif, svg, avif.
 */
export function extractImages(content: string): string[] {
    const imgRegex = /https?:\/\/[a-zA-Z0-9\-\._~:\/?#[\]@!$&'()*+,;=%]+\.(?:jpg|jpeg|png|webp|gif|svg|avif)/gi
    return content.match(imgRegex) || []
}
